import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Gauge,
  Sparkles,
  Telescope,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import AbstractShapes from "@/components/landing/AbstractShapes";

const FEATURES = [
  {
    icon: Gauge,
    title: "Impact Index",
    range: "0 – 100",
    desc: "AI methodology evaluation (≤70) × Data Integrity multiplier + visibility from citations, downloads and social signals (≤30).",
    bullets: [
      "AI-powered methodology scoring",
      "Data Integrity check as multiplier",
      "Clinical Learning Value for case reports",
    ],
  },
  {
    icon: Sparkles,
    title: "AI Research Analysis",
    range: "PICO + GAPS",
    desc: "Structured knowledge extraction from every article — core idea, PICO framework, Data Integrity flags, future-direction gaps.",
    bullets: [
      "Core scientific idea extraction",
      "PICO framework parsing",
      "Research gaps & future directions",
    ],
  },
  {
    icon: Telescope,
    title: "Public Explorer",
    range: "OPEN",
    desc: "Compare and rank articles by methodology impact and visibility. PDF downloads with tracking, author profiles, and a global research map.",
    bullets: [
      "Filter by study type & evidence level",
      "Author profiles + global map",
      "PDF download tracking",
    ],
  },
];

// Live impact-index gauges, real numbers from evidentica.net homepage
const JOURNAL_IMPACT = [
  {
    short: "IJSM",
    full: "Int. Journal of Surgery and Medicine",
    index: 28.1,
    articles: 8,
    explorerUrl: "https://evidentica.net/explorer",
  },
  {
    short: "IJMRCR",
    full: "Int. Journal of Medical Reviews and Case Reports",
    index: 16.1,
    articles: 12,
    explorerUrl: "https://evidentica.net/explorer",
  },
];

function ImpactGauge({ index, articles, short, full, url }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const dur = 1400;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setProgress(1 - Math.pow(1 - p, 3));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [index]);

  const pct = (index / 100) * progress;
  const R = 60;
  const C = 2 * Math.PI * R;
  const offset = C - (pct / 100) * C;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`evidentica-gauge-${short.toLowerCase()}`}
      className="group block border border-white/10 hover:border-[var(--sci-teal)] transition-colors p-6 md:p-8 bg-white/[0.03]"
    >
      <div className="flex items-start gap-6">
        <svg width="148" height="148" viewBox="0 0 148 148" className="shrink-0">
          {/* Track */}
          <circle
            cx="74"
            cy="74"
            r={R}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="6"
            fill="none"
          />
          {/* Progress */}
          <circle
            cx="74"
            cy="74"
            r={R}
            stroke="var(--sci-teal)"
            strokeWidth="6"
            fill="none"
            strokeDasharray={C}
            strokeDashoffset={offset}
            strokeLinecap="butt"
            transform="rotate(-90 74 74)"
            style={{ transition: "stroke-dashoffset 0.2s ease-out" }}
          />
          {/* Center label */}
          <text
            x="74"
            y="76"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontSize: "30px",
              fontWeight: 600,
              fill: "white",
            }}
          >
            {(index * progress).toFixed(1)}
          </text>
          <text
            x="74"
            y="96"
            textAnchor="middle"
            style={{
              fontFamily: "IBM Plex Mono, monospace",
              fontSize: "8.5px",
              letterSpacing: "0.18em",
              fill: "rgba(255,255,255,0.55)",
            }}
          >
            / 100
          </text>
        </svg>
        <div className="min-w-0 flex-1">
          <div className="overline text-white/55 mb-2">{short}</div>
          <div className="font-serif-display text-xl md:text-2xl text-white leading-tight">
            {full}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] font-mono-sci tracking-[0.16em] uppercase">
            <span className="border border-white/15 px-2 py-1 text-white/75">
              {articles} ARTICLES ANALYZED
            </span>
            <span className="border border-[var(--sci-teal)]/40 px-2 py-1 text-[var(--sci-teal)]">
              IMPACT INDEX
            </span>
          </div>
          <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-white group-hover:text-[var(--sci-teal)] transition-colors">
            Open in Explorer
            <ArrowUpRight size={14} />
          </div>
        </div>
      </div>
    </a>
  );
}

export default function EvidenticaProject() {
  return (
    <section
      id="evidentica"
      data-testid="evidentica-section"
      className="relative py-24 md:py-32 bg-[var(--sci-navy)] text-white overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
      <AbstractShapes
        variant="constellation"
        tone="white"
        className="right-0 top-10 w-[420px] h-[420px] opacity-30"
      />
      <div
        className="absolute -left-32 bottom-0 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(0,122,138,0.35), transparent)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14 md:mb-20 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="overline text-[var(--sci-teal)]">
                · 07 / Initiative · 2026
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-sci tracking-[0.2em] uppercase border border-white/20 px-2 py-1 text-white/70">
                <span className="w-1.5 h-1.5 bg-[var(--sci-teal)] sci-pulse-dot rounded-full" />
                In Development
              </span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Evidentica<span className="text-[var(--sci-teal)]">.</span> <br />
              <em className="not-italic text-[var(--sci-teal)]">Research visibility.</em> Reimagined.
            </h2>
          </div>
          <p className="lg:col-span-5 text-white/70 leading-relaxed text-base md:text-lg">
            An upcoming Sci Ink Press initiative — AI-powered methodology
            scoring and structured analysis for every article we publish.
            Open Explorer, transparent metrics, no black-box journal rankings.
          </p>
        </div>

        {/* Live impact gauges */}
        <div className="mb-16 md:mb-20">
          <div className="overline text-white/55 mb-5">
            · Currently analyzing both Sci Ink Press journals
          </div>
          <div className="grid lg:grid-cols-2 gap-5 md:gap-6">
            {JOURNAL_IMPACT.map((j) => (
              <ImpactGauge
                key={j.short}
                index={j.index}
                articles={j.articles}
                short={j.short}
                full={j.full}
                url={j.explorerUrl}
              />
            ))}
          </div>
        </div>

        {/* Feature trio */}
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, delay: 0.06 * i }}
                data-testid={`evidentica-feature-${i}`}
                className="bg-[var(--sci-navy)] p-8 md:p-10 group hover:bg-[var(--sci-navy-soft)] transition-colors"
              >
                <div className="flex items-start justify-between mb-7">
                  <div className="w-11 h-11 border border-white/20 flex items-center justify-center text-[var(--sci-teal)] group-hover:border-[var(--sci-teal)] transition-colors">
                    <Icon size={18} strokeWidth={1.5} />
                  </div>
                  <div className="font-mono-sci text-[10px] tracking-[0.22em] text-[var(--sci-teal)]/80">
                    {f.range}
                  </div>
                </div>
                <h3 className="font-serif-display text-2xl md:text-[28px] leading-tight tracking-tight">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">
                  {f.desc}
                </p>
                <ul className="mt-6 space-y-2">
                  {f.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-xs text-white/75"
                    >
                      <ChevronRight
                        size={12}
                        className="text-[var(--sci-teal)] mt-0.5 shrink-0"
                      />
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Footer strip */}
        <div className="mt-14 grid md:grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="md:col-span-7 flex items-start gap-3">
            <ShieldCheck
              size={16}
              className="text-[var(--sci-teal)] shrink-0 mt-0.5"
            />
            <p className="text-xs text-white/55 leading-relaxed max-w-2xl">
              The Impact Index is a methodological indicator designed to assess
              study-design strength and research visibility. It does not
              represent clinical truth, guideline recommendations, or treatment
              standards.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-wrap gap-3 justify-start md:justify-end">
            <a
              href="https://evidentica.net/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="evidentica-visit"
              className="btn-on-dark inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide"
            >
              Visit Evidentica
              <ArrowUpRight size={14} />
            </a>
            <a
              href="https://evidentica.net/explorer"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="evidentica-explorer"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide border border-white/40 hover:border-white text-white/95 hover:text-white transition-colors"
            >
              Open Explorer
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
